import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import AdminLayout from '../../components/AdminLayout';
import DateInput from '../../components/DateInput';
import {
  DEFAULT_TIME_ZONE,
  zonedTimeToUtc
} from '../../utils/timezone';

function CampaignCreate() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [allProducts, setAllProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [availableMechanics, setAvailableMechanics] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    custom_domain: '',
    start_date: '',
    end_date: '',
    mechanics_type_id: '',
    max_entries_per_user: 1,
    unlimited_entries: false,
    requires_receipt: true,
    skip_receipt_validation: false,
    entry_limit_period: 'none',
    entry_limit_timezone: 'America/New_York'
  });


  useEffect(() => {
    fetchAllProducts();
    fetchAvailableMechanics();
  }, []);

  const fetchAllProducts = async () => {
    try {
      const response = await api.get('/admin/products');
      setAllProducts(response.data.products || []);
    } catch (err) {
      console.error('Fetch products error:', err);
    }
  };

  const fetchAvailableMechanics = async () => {
    try {
      const response = await api.get('/admin/mechanics/all');
      console.log('Available mechanics:', response.data.mechanics);
      setAvailableMechanics(response.data.mechanics || []);
    } catch (err) {
      console.error('Failed to fetch available mechanics:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    // Client-side validation
    const validationErrors = [];
    
    if (!formData.name || formData.name.trim() === '') {
      validationErrors.push('Campaign name is required');
    }
    
    if (!formData.start_date) {
      validationErrors.push('Start date is required');
    }
    
    if (!formData.end_date) {
      validationErrors.push('End date is required');
    }
    
    if (formData.start_date && formData.end_date) {
      // Basic lexical check: ensure format and ordering
      const valid = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(formData.start_date) && /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(formData.end_date);
      if (!valid) {
        validationErrors.push('Invalid start or end date');
      } else if (formData.start_date >= formData.end_date) {
        validationErrors.push('End date must be after start date');
      }
    }
    
    if (!formData.mechanics_type_id) {
      validationErrors.push('Mechanics type is required');
    }
    
    if (!formData.unlimited_entries && (!formData.max_entries_per_user || parseInt(formData.max_entries_per_user) < 1)) {
      validationErrors.push('Maximum entries per user must be at least 1');
    }

    if (formData.custom_domain && formData.custom_domain.trim() !== '') {
      const trimmedDomain = formData.custom_domain.trim();
      if (trimmedDomain.startsWith('http://') || trimmedDomain.startsWith('https://')) {
        validationErrors.push('Custom domain should not include http:// or https://');
      } else if (!/^[a-zA-Z0-9.-]+(:\d+)?$/.test(trimmedDomain)) {
        validationErrors.push('Custom domain can only contain letters, numbers, dots, hyphens, and an optional port');
      }
    }
    
    if (validationErrors.length > 0) {
      setError(validationErrors.join('. '));
      setLoading(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    try {
      if (!formData.start_date || !formData.end_date) {
        setError('Invalid start or end date');
        setLoading(false);
        return;
      }

      const formattedData = {
        ...formData,
        // Send local Sydney time as provided (backend will format to MySQL DATETIME)
        start_date: formData.start_date,
        end_date: formData.end_date,
        max_entries_per_user: formData.unlimited_entries ? 0 : parseInt(formData.max_entries_per_user)
      };

      formattedData.custom_domain = formData.custom_domain && formData.custom_domain.trim() !== ''
        ? formData.custom_domain.trim().toLowerCase()
        : null;

      // Remove the unlimited_entries flag before sending
      delete formattedData.unlimited_entries;

      console.log('Sending campaign data:', formattedData);
      const response = await api.post('/admin/campaigns', formattedData);
      const campaignId = response.data.campaign?.id;
      
      if (campaignId) {
        // Save selected products if any
        if (selectedProducts.length > 0) {
          await api.put(`/admin/campaigns/${campaignId}/products`, {
            product_ids: selectedProducts
          });
        }
      }
      
      navigate('/admin/campaigns');
    } catch (err) {
      console.error('Create campaign error:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error details:', JSON.stringify(err.response?.data?.errors, null, 2));
      
      // Handle validation errors from backend
      if (err.response?.data?.errors && Array.isArray(err.response.data.errors)) {
        const backendErrors = err.response.data.errors.map(e => {
          // Handle express-validator errors
          if (e.param && e.msg) {
            const fieldName = e.param.replace(/_/g, ' ');
            return `${fieldName}: ${e.msg}`;
          }
          return e.msg || e.message || 'Unknown error';
        }).join('. ');
        setError(backendErrors);
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.response?.status === 400) {
        setError('Invalid input. Please check all required fields.');
      } else {
        setError('Failed to create campaign. Please check all required fields and try again.');
      }
      
      setLoading(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <AdminLayout activeSection="CAMPAIGN">
      {/* Header with back button */}
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate('/admin/campaigns')}
          className="text-white hover:text-gray-200 mr-4"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
        </button>
        <h1 className="text-3xl font-bold text-white">CREATE / NEW CAMPAIGN</h1>
      </div>
      
      <div className="bg-white rounded-3xl shadow-2xl overflow-hidden p-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="p-4 bg-red-50 border-2 border-red-300 rounded-xl shadow-lg">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Validation Error</h3>
                  <div className="mt-1 text-sm text-red-700">{error}</div>
                </div>
              </div>
            </div>
          )}


          {/* Date Selection */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="inline-block px-4 py-2 bg-teal-700 text-white rounded-lg text-sm font-medium mb-3">
                Start Date
              </label>
              <DateInput
                name="start_date"
                required
                value={formData.start_date}
                onChange={handleChange}
                className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="inline-block px-4 py-2 bg-teal-700 text-white rounded-lg text-sm font-medium mb-3">
                End Date
              </label>
              <DateInput
                name="end_date"
                required
                value={formData.end_date}
                onChange={handleChange}
                className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Campaign Details */}
          <div>
            <label className="inline-block px-4 py-2 bg-teal-700 text-white rounded-lg text-sm font-medium mb-3">
              Campaign Name
            </label>
            <input
              type="text"
              name="name"
              required
              value={formData.name}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="Enter campaign name..."
            />
          </div>

          <div>
            <label className="inline-block px-4 py-2 bg-teal-700 text-white rounded-lg text-sm font-medium mb-3">
              Custom Domain (optional)
            </label>
            <input
              type="text"
              name="custom_domain"
              value={formData.custom_domain}
              onChange={handleChange}
              placeholder="e.g., winbali.com"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-2">
              Provide only the domain or subdomain; do not include http:// or https://.
            </p>
            <div className="mt-3 bg-blue-50 border border-blue-200 text-blue-800 text-xs rounded-lg p-3 space-y-3">
              <p>
                Point the domain at the same host that already serves your campaign (e.g. <code>gooddrinkspromotion.dailypress.com.au</code>). Use the records below as a guide and ensure the domain has a valid SSL certificate before going live.
              </p>
              <div className="overflow-hidden rounded-lg border border-blue-200 bg-white text-gray-700">
                <table className="min-w-full text-left text-xs">
                  <thead className="bg-blue-100 text-blue-900 uppercase tracking-wider">
                    <tr>
                      <th className="px-4 py-2 font-semibold">Type</th>
                      <th className="px-4 py-2 font-semibold">Name</th>
                      <th className="px-4 py-2 font-semibold">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t border-blue-100">
                      <td className="px-4 py-2 text-blue-900 font-medium">A</td>
                      <td className="px-4 py-2">@ (root)</td>
                      <td className="px-4 py-2 font-mono">34.40.151.217</td>
                    </tr>
                    <tr className="border-t border-blue-100">
                      <td className="px-4 py-2 text-blue-900 font-medium">CNAME</td>
                      <td className="px-4 py-2">www (optional)</td>
                      <td className="px-4 py-2 font-mono">gooddrinkspromotion.dailypress.com.au</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div>
            <label className="inline-block px-4 py-2 bg-teal-700 text-white rounded-lg text-sm font-medium mb-3">
              Winning Mechanic
            </label>
            <select
              name="mechanics_type_id"
              value={formData.mechanics_type_id}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-gray-600"
            >
              <option value="">Select option from the dropdown menu</option>
              {availableMechanics.map(type => (
                <option key={type.id} value={type.id}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="inline-block px-4 py-2 bg-teal-700 text-white rounded-lg text-sm font-medium mb-3">
              Campaign Description
            </label>
            <textarea
              name="description"
              rows={4}
              value={formData.description}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="Enter campaign description..."
            />
          </div>

          {/* Settings */}
          <div className="border-t pt-6 space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Additional Settings</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Entries Per User
                </label>
                <div className="space-y-2">
                  <input
                    type="number"
                    name="max_entries_per_user"
                    min="1"
                    value={formData.max_entries_per_user}
                    onChange={handleChange}
                    disabled={formData.unlimited_entries}
                    className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                      formData.unlimited_entries ? 'bg-gray-100 text-gray-500' : ''
                    }`}
                  />
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="unlimited_entries"
                      checked={formData.unlimited_entries}
                      onChange={handleChange}
                      className="h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    />
                    <span className="ml-2 text-sm text-gray-600">Allow unlimited entries per user</span>
                  </label>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Entry Period
                </label>
                <select
                  name="entry_limit_period"
                  value={formData.entry_limit_period}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                >
                  <option value="none">Total (No Reset)</option>
                  <option value="daily">Per Day</option>
                  <option value="weekly">Per Week</option>
                </select>
              </div>
            </div>

            <div className="space-y-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="requires_receipt"
                  checked={formData.requires_receipt}
                  onChange={handleChange}
                  className="h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                />
                <span className="ml-2 text-sm text-gray-700">Requires Receipt Upload</span>
              </label>
              
              {formData.requires_receipt && (
                <label className="flex items-center ml-6">
                  <input
                    type="checkbox"
                    name="skip_receipt_validation"
                    checked={formData.skip_receipt_validation}
                    onChange={handleChange}
                    className="h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Skip receipt validation</span>
                </label>
              )}
              
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end space-x-3 pt-6">
            <button
              type="button"
              onClick={() => navigate('/admin/campaigns')}
              className="px-6 py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-8 py-3 bg-teal-700 text-white rounded-xl hover:bg-teal-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : 'Create Campaign'}
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
}

export default CampaignCreate;
